/**
 * Data contract between the playback state machine and wherever tours come
 * from. Phase F implements this with `HardcodedTourSource` (see
 * `src/data/HardcodedTourSource.ts`); Phase G will add an `ApiTourSource`
 * that calls `GET /solutions`. `PlaybackController` depends only on this
 * interface, so swapping the source is the entire Phase G frontend change —
 * no state machine, camera, or animation code needs to move.
 *
 * The browser never computes `solutionHash`/canonical identity itself; it is
 * always a value handed to it by a `TourSource` (in Phase F, a hard-coded
 * fixture id standing in for one; in Phase G, the real API-issued hash).
 */

export interface TourData {
  /** Stable identifier for this tour, used to avoid immediate replay. */
  id: string;
  /** Exactly 64 zero-based square identifiers (0..63). */
  tour: number[];
  isClosed: boolean;
  score: number;
  timesFound: number;
  firstGeneration: number;
  lastGeneration: number;
  /** Derived, never authored by the browser: 1 for open, 64 for closed. */
  representedCount: number;
}

export interface NextTourOptions {
  /**
   * Id of the tour that just finished playing; implementations should
   * avoid immediately returning the same id again when more than one
   * solution is available.
   */
  excludeId?: string;
  /**
   * If provided, the caller wants playback to continue tile-to-tile
   * without a teleport: prefer a tour whose own first move begins exactly
   * at this square (0..63) over the implementation's normal selection
   * strategy. Not a hard requirement -- if no such tour is available (e.g.
   * an empty database, or only unrelated open tours), implementations fall
   * back to their normal strategy rather than failing.
   */
  continueFromSquare?: number;
}

export interface TourSource {
  /**
   * Fetch the next tour to play.
   *
   * Rejects with `NoSolutionsAvailableError` when there is genuinely
   * nothing to play yet (e.g. an empty database) — distinct from a
   * transport failure, so the UI can show an unobtrusive "waiting" state
   * instead of an error banner.
   */
  next(options?: NextTourOptions): Promise<TourData>;
}

/**
 * Signals "there is nothing to play right now," as opposed to "something
 * went wrong trying to find out." Any `TourSource` implementation can
 * throw this — it is not specific to the API-backed one.
 */
export class NoSolutionsAvailableError extends Error {
  constructor(message = "no Knight's Tour solutions are available yet") {
    super(message);
    this.name = "NoSolutionsAvailableError";
  }
}
